import React from "react";
import {
  Link,
  type LinkProps,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { isPrerender } from "../utils/ga";
import {
  type AppLocale,
  DEFAULT_LOCALE,
  getLocaleFromPath,
  localizePath,
  LOCALE_META,
  readStoredLocale,
  stripLocalePrefix,
  writeStoredLocale,
} from "./locales";
import i18n from "./index";

export function useAppLocale(): AppLocale {
  return getLocaleFromPath(useLocation().pathname);
}

export function useLocalePath() {
  const locale = useAppLocale();
  return React.useCallback(
    (pathname: string, search = "", hash = "") =>
      localizePath(pathname, locale, search, hash),
    [locale],
  );
}

export function useSwitchLocale() {
  const navigate = useNavigate();
  const location = useLocation();

  return React.useCallback(
    (next: AppLocale) => {
      writeStoredLocale(next);
      void i18n.changeLanguage(next);
      document.documentElement.lang = LOCALE_META[next].htmlLang;
      navigate(
        localizePath(
          location.pathname,
          next,
          location.search,
          location.hash,
        ),
      );
    },
    [location.hash, location.pathname, location.search, navigate],
  );
}

/** Link that prefixes the current locale onto absolute site paths. */
export const LocalizedLink = React.forwardRef<
  HTMLAnchorElement,
  LinkProps & { to: string }
>(function LocalizedLink({ to, ...rest }, ref) {
  const localePath = useLocalePath();
  const resolved =
    typeof to === "string" && to.startsWith("/")
      ? localePath(to)
      : to;
  return <Link ref={ref} to={resolved} {...rest} />;
});

/**
 * Syncs i18n + <html lang> with the URL locale, and redirects once when
 * localStorage preference differs from the current URL (skipped in prerender).
 *
 * IMPORTANT: always render <Outlet />. Returning null on the first client paint
 * breaks react-snap hydration (prerendered HTML stays + React remounts another
 * copy → double header/content; map stuck on "Map preview" placeholder).
 */
export function LocaleLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const urlLocale = getLocaleFromPath(location.pathname);

  React.useEffect(() => {
    void i18n.changeLanguage(urlLocale);
    document.documentElement.lang = LOCALE_META[urlLocale].htmlLang;
  }, [urlLocale]);

  React.useEffect(() => {
    if (isPrerender()) return;

    const stored = readStoredLocale();
    // Only upgrade default-locale URLs to a stored preference.
    // Never redirect away from an explicit /ja or /zh-TW URL (share + SEO stable).
    if (
      stored &&
      stored !== urlLocale &&
      urlLocale === DEFAULT_LOCALE &&
      stored !== DEFAULT_LOCALE
    ) {
      navigate(
        localizePath(
          location.pathname,
          stored,
          location.search,
          location.hash,
        ),
        { replace: true },
      );
    }
    // Only on first mount / when landing without matching preference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <Outlet />;
}

/** Optional helper for pages that need a bare path for matching. */
export function useBarePathname() {
  return stripLocalePrefix(useLocation().pathname);
}

export function LocaleDefaultRedirect({ to }: { to: string }) {
  const localePath = useLocalePath();
  return <Navigate to={localePath(to)} replace />;
}

export { DEFAULT_LOCALE, localizePath, stripLocalePrefix, getLocaleFromPath };
